import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { Component } from "react";
import { MerchantContact } from "../../models/MerchantModel";
import { SelectItem } from "../../models/SelectItem";
import { merchantContactApiService } from "../../services/api/merchants/MerchantContactsApiService";
import AddMerchantContact from "./AddMerchantContact";
import { toastService } from "../../services/toastService";
import _ from "lodash";
import { confirmDanger } from "../../utils/confirmHelpers";
import { isMerchantProfileUpdateAllowed } from "../../authGuard";
import { Pencil, Plus, X } from "lucide-react";

interface MerchantContactProps {
    merchantId: number;
    localMode: boolean;
    onLocalContactUpdated?: (contact: MerchantContact[]) => void;
    merchantContacts?: MerchantContact[];
}

interface MerchantContactState {
    contacts: MerchantContact[];
    isLoading: boolean;
    isContactDetailVisible: boolean;
    selectedContact: MerchantContact | null;
}

export default class MerchantContactComponent extends Component<MerchantContactProps, MerchantContactState> {
    contactTypes: SelectItem[] = [];

    constructor(props: MerchantContactProps | Readonly<MerchantContactProps>) {
        super(props);

        this.state = {
            contacts: this.props.localMode ? this.props.merchantContacts! : [],
            isLoading: true,
            isContactDetailVisible: false,
            selectedContact: null,
        };
    }

    async componentDidMount() {
        const contactTypes = await merchantContactApiService.getContactTypes();

        if (!contactTypes.success) {
            toastService.error(contactTypes.message);
            this.setState({ isLoading: false });
            return;
        }

        this.contactTypes = contactTypes.data;

        this.setState({
            isLoading: false,
        });

        await this.loadContacts();
    }

    loadContacts = async () => {
        if (this.props.localMode) {
            return;
        }

        this.setState({ isLoading: true });

        const contacts = await merchantContactApiService.getAll(this.props.merchantId);

        this.setState({ isLoading: false });

        if (!contacts.success) {
            toastService.error(contacts.message);
            return;
        }

        this.setState({ contacts: contacts.data });
    };

    renderHeader = () => {
        return (
            <div className="table-header">
                <span className="p-input-icon-left align-right">
                    {isMerchantProfileUpdateAllowed() && (
                        <Button
                            label="Add Contact"
                            outlined
                            icon={<Plus size={16} />}
                            onClick={() => this.addorEditContact(this.getEmptyContact())}
                        />
                    )}
                </span>
            </div>
        );
    };

    getEmptyContact = (): MerchantContact => {
        return { id: 0, name: "", phone: "", email: "", type: "", merchantId: this.props.merchantId };
    };

    addorEditContact = (rowData: MerchantContact) => {
        this.setState({ isContactDetailVisible: true, selectedContact: rowData });
    };

    onDetailsModalClose = async (contact: MerchantContact) => {
        this.closeContactModel();
        if (this.props.localMode) {
            const contacts = this.state.contacts;
            if (contacts.length < this.contactTypes.length) {
                if (this.contactTypes.find((i) => i.value == contact.type)) {
                    if (contacts.find((i) => i.type === contact.type)) {
                        toastService.error("Contact Type already exists");
                    } else {
                        contacts.push(contact!);
                        this.setState({ contacts: contacts });
                        this.props.onLocalContactUpdated!(contacts);
                    }
                } else {
                    toastService.error("Contact type is not valid.");
                }
            } else {
                toastService.error("Contact limit exceeds.");
            }
        }
        await this.loadContacts();
    };

    typeBodyTemplate = (rowData: MerchantContact): string => {
        const list = _.filter(this.contactTypes, (c) => c.value == rowData.type);

        return list.length > 0 ? list[0].label : "N/A";
    };

    confirmDeleteContact = async (contact: MerchantContact) => {
        if (this.props.localMode) {
            const contacts = this.state.contacts;
            const index = contacts.findIndex(
                (f) => f.email == contact.email && f.name == contact.name && f.phone == contact.phone && f.type == contact.type,
            );

            if (index >= 0) {
                contacts.splice(index, 1);
            }

            this.setState({ contacts: contacts });

            this.props.onLocalContactUpdated!(this.state.contacts);
            return;
        }

        confirmDanger(
            "This action will remove the contact and is irreversible. Are you sure you want to proceed?",
            "Delete Contact",
            () => this.deleteContact(contact),
        );
    };

    deleteContact = async (rowData: MerchantContact) => {
        const deleteResult = await merchantContactApiService.delete(rowData.id);

        if (deleteResult.success) {
            this.setState({ contacts: this.state.contacts.filter((contact) => contact.id !== rowData.id) });
        } else {
            toastService.error(deleteResult.message);
        }
    };

    actionBodyTemplate = (rowData: MerchantContact) => {
        return (
            <>
                {this.props.localMode == false && (
                    <button type="button" className="inline-flex items-center justify-center rounded-md p-2 hover:bg-neutral-100 transition-colors" tabIndex={0} onClick={() => this.addorEditContact(rowData)}>
                        <Pencil size={16} className="cursor-pointer text-neutral-500 hover:text-primary-500 transition-colors" />
                    </button>
                )}
                <button type="button" className="inline-flex items-center justify-center rounded-md p-2 hover:bg-neutral-100 transition-colors" tabIndex={0} onClick={() => this.confirmDeleteContact(rowData)}>
                    <X size={16} className="cursor-pointer text-neutral-500 hover:text-danger-500 transition-colors" />
                </button>
            </>
        );
    };

    closeContactModel = () => {
        this.setState({ isContactDetailVisible: false });
    };

    render() {
        return (
            <div>
                {this.state.isContactDetailVisible && this.state.selectedContact != null && (
                    <AddMerchantContact
                        onHide={this.closeContactModel}
                        merchantId={this.props.merchantId}
                        contact={this.state.selectedContact}
                        types={this.contactTypes}
                        localMode={this.props.localMode}
                        onSubmit={(contact) => this.onDetailsModalClose(contact)}
                    />
                )}
                <DataTable header={this.renderHeader} value={this.state.contacts} loading={this.state.isLoading}>
                    <Column field="id" header="Id"></Column>
                    <Column field="name" header="Contact Name" style={{ width: "20%" }}></Column>
                    <Column field="phone" header="Contact phone" style={{ width: "20%" }}></Column>
                    <Column field="email" header="Contact Email" style={{ width: "20%" }}></Column>
                    <Column field="type" header="Type" body={this.typeBodyTemplate} style={{ width: "20%" }}></Column>
                    <Column
                        header=""
                        body={this.actionBodyTemplate}
                        headerStyle={{ width: "8em", textAlign: "center" }}
                        bodyStyle={{ textAlign: "center", overflow: "visible" }}
                    />
                </DataTable>
            </div>
        );
    }
}
